import React, { useMemo } from 'react';
import './ArtifactSummary.css';

/**
 * ArtifactSummary Component
 * 
 * Campaign-wide view showing all deliverables and their consumers.
 * Shows:
 * - All deliverables grouped by source mission
 * - Which missions consume each deliverable
 * - Orphaned deliverables (produced but never used)
 * - External inputs
 */
const ArtifactSummary = ({ tasks = [], isOpen, onClose }) => {
  // Build the artifact flow map
  const artifactData = useMemo(() => {
    const deliverables = []; // { name, link, sourceMission, consumers: [] }
    const externalInputs = []; // { name, consumers: [] }
    
    // First pass: collect all deliverables
    tasks.forEach(task => {
      if (task.deliverables && task.deliverables.length > 0) {
        task.deliverables.forEach(del => {
          deliverables.push({
            id: del.id,
            name: del.name,
            link: del.link,
            sourceMissionId: task.id,
            sourceMissionName: task.name,
            sourceMissionStatus: task.status,
            consumers: [],
          });
        });
      }
    });
    
    // Second pass: find consumers for each deliverable and collect external inputs
    tasks.forEach(task => {
      if (task.inputs && task.inputs.length > 0) {
        task.inputs.forEach(input => {
          if (input.sourceType === 'external') {
            // Check if we already have this external input
            let existing = externalInputs.find(e => e.name === input.name);
            if (!existing) {
              existing = { name: input.name, consumers: [] };
              externalInputs.push(existing);
            }
            existing.consumers.push({
              missionId: task.id,
              missionName: task.name,
              missionStatus: task.status,
            });
          } else if (input.sourceType === 'mission' && input.sourceDeliverableId) {
            // Find the deliverable and add this task as a consumer
            const deliverable = deliverables.find(d => d.id === input.sourceDeliverableId);
            if (deliverable) {
              deliverable.consumers.push({
                missionId: task.id,
                missionName: task.name,
                missionStatus: task.status,
              });
            }
          }
        });
      }
    });
    
    // Group deliverables by source mission
    const deliverablesByMission = {};
    deliverables.forEach(del => {
      if (!deliverablesByMission[del.sourceMissionId]) {
        deliverablesByMission[del.sourceMissionId] = {
          missionName: del.sourceMissionName,
          missionStatus: del.sourceMissionStatus,
          deliverables: [],
        };
      }
      deliverablesByMission[del.sourceMissionId].deliverables.push(del);
    });
    
    // Find orphaned deliverables (no consumers)
    const orphaned = deliverables.filter(d => d.consumers.length === 0);
    
    return {
      deliverablesByMission,
      externalInputs,
      orphaned,
      totalDeliverables: deliverables.length,
      totalCompleted: deliverables.filter(d => d.completed).length,
    };
  }, [tasks]);

  if (!isOpen) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✓';
      case 'in_progress': return '●';
      case 'blocked': return '⊘';
      default: return '○';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'artifact-summary__status--completed';
      case 'in_progress': return 'artifact-summary__status--active';
      case 'blocked': return 'artifact-summary__status--blocked';
      default: return 'artifact-summary__status--pending';
    }
  };

  return (
    <div className="artifact-summary__overlay" onClick={onClose}>
      <div className="artifact-summary__panel" onClick={(e) => e.stopPropagation()}>
        <div className="artifact-summary__header">
          <div className="artifact-summary__header-top">
            <h2 className="artifact-summary__title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17l-4-4 4-4" />
                <path d="M17 7l4 4-4 4" />
                <line x1="3" y1="13" x2="15" y2="13" />
                <line x1="9" y1="11" x2="21" y2="11" />
              </svg>
              Ins & Outs
            </h2>
            <button className="artifact-summary__close" onClick={onClose}>×</button>
          </div>
          <p className="artifact-summary__description">
            Everything that flows through this campaign — external inputs that come in from outside, 
            and the deliverables each mission produces along the way.
          </p>
          <div className="artifact-summary__stats">
            <span className="artifact-summary__stat">
              {artifactData.totalCompleted}/{artifactData.totalDeliverables} completed
            </span>
            {artifactData.orphaned.length > 0 && (
              <span className="artifact-summary__stat artifact-summary__stat--warning">
                {artifactData.orphaned.length} unused
              </span>
            )}
          </div>
        </div>

        <div className="artifact-summary__content">
          {/* Deliverables by Mission */}
          <div className="artifact-summary__section">
            <h3 className="artifact-summary__section-title">Deliverables by Mission</h3>
            {Object.keys(artifactData.deliverablesByMission).length === 0 ? (
              <div className="artifact-summary__empty">No deliverables defined yet</div>
            ) : (
              Object.entries(artifactData.deliverablesByMission).map(([missionId, data]) => (
                <div key={missionId} className="artifact-summary__mission-group">
                  <div className="artifact-summary__mission-header">
                    <span className={`artifact-summary__status ${getStatusClass(data.missionStatus)}`}>
                      {getStatusIcon(data.missionStatus)}
                    </span>
                    <span className="artifact-summary__mission-name">{data.missionName}</span>
                  </div>
                  <div className="artifact-summary__deliverables">
                    {data.deliverables.map(del => (
                      <div key={del.id} className="artifact-summary__deliverable">
                        <div className="artifact-summary__deliverable-header">
                          <span className={`artifact-summary__deliverable-name ${del.completed ? 'artifact-summary__deliverable-name--completed' : ''}`}>
                            {del.completed ? '✓' : '○'} {del.name}
                          </span>
                          {del.link && (
                            <a 
                              href={del.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="artifact-summary__link"
                              title={del.link}
                            >
                              Open →
                            </a>
                          )}
                        </div>
                        {del.consumers.length > 0 ? (
                          <div className="artifact-summary__consumers">
                            <span className="artifact-summary__consumers-label">Used by:</span>
                            {del.consumers.map((consumer, idx) => (
                              <span 
                                key={consumer.missionId} 
                                className={`artifact-summary__consumer ${getStatusClass(consumer.missionStatus)}`}
                              >
                                {consumer.missionName}
                                {idx < del.consumers.length - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="artifact-summary__consumers artifact-summary__consumers--orphan">
                            <span className="artifact-summary__consumers-label">⚠️ Not used by any mission</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* External Inputs */}
          {artifactData.externalInputs.length > 0 && (
            <div className="artifact-summary__section">
              <h3 className="artifact-summary__section-title">External Inputs</h3>
              <div className="artifact-summary__external-list">
                {artifactData.externalInputs.map((input, idx) => (
                  <div key={idx} className="artifact-summary__external-item">
                    <span className="artifact-summary__external-name">
                      📥 {input.name}
                    </span>
                    <div className="artifact-summary__consumers">
                      <span className="artifact-summary__consumers-label">Used by:</span>
                      {input.consumers.map((consumer, cidx) => (
                        <span 
                          key={consumer.missionId} 
                          className={`artifact-summary__consumer ${getStatusClass(consumer.missionStatus)}`}
                        >
                          {consumer.missionName}
                          {cidx < input.consumers.length - 1 && ', '}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtifactSummary;

